import {ActiveUserAddress} from '@domain/model/Account/UserAddress/ActiveUserAddress';
import {UpdateUserAddressCommand} from '@domain/model/Account/UserAddress/UpdateUserAddressCommand';
import {ViewActiveUserAddressByUserIdQuery} from '@domain/model/Account/UserAddress/ViewActiveUserAddressByUserIdQuery';
import {getAuthHeader} from '@infra/api/authManager/authManager';
import {fetch} from '@infra/api/fetch';

import {DEFAULT_HEADERS} from '../../_utils/defaultHeaders';

const USER_ADDRESS_URL = '/logistics/update-user-address';
const ACTIVE_USER_ADDRESS_URL = '/logistics/view-active-user-address-by-user-id';

const normalizeResponse = (response: unknown): Record<string, boolean> => {
  if (typeof response === 'boolean') {
    return {success: response};
  }

  if (response && typeof response === 'object') {
    return response as Record<string, boolean>;
  }

  return {success: true};
};

const parseResponse = async (result: Response): Promise<unknown> => {
  const rawResponse = await result.text();

  if (!rawResponse) {
    return {};
  }

  try {
    return JSON.parse(rawResponse);
  } catch {
    return {};
  }
};

const setUserAddress = async (userAddress: UpdateUserAddressCommand): Promise<Record<string, boolean>> => {
  const authHeader = await getAuthHeader();

  try {
    const result = await fetch(USER_ADDRESS_URL, {
      method: 'POST',
      headers: {
        ...authHeader,
        ...DEFAULT_HEADERS,
      },
      body: JSON.stringify(userAddress),
    });

    const response = await parseResponse(result);

    if (!result.ok) {
      return Promise.reject(response);
    }

    return normalizeResponse(response);
  } catch (error) {
    return Promise.reject(error);
  }
};

const parseActiveUserAddressResponse = async (result: Response): Promise<unknown> => {
  const rawResponse = await result.text();

  if (!rawResponse) {
    return null;
  }

  try {
    return JSON.parse(rawResponse);
  } catch {
    return null;
  }
};

const normalizeActiveUserAddress = (response: unknown): ActiveUserAddress | null => {
  if (!response || typeof response !== 'object') {
    return null;
  }

  const {address} = response as {address?: unknown};
  if (typeof address !== 'string' || address.length === 0) {
    return null;
  }

  return {...(response as Record<string, unknown>), address} as ActiveUserAddress;
};

const viewActiveUserAddressByUserId = async ({
  userId,
}: ViewActiveUserAddressByUserIdQuery): Promise<ActiveUserAddress | null> => {
  const authHeader = await getAuthHeader();

  try {
    const result = await fetch(ACTIVE_USER_ADDRESS_URL, {
      method: 'POST',
      headers: {
        ...authHeader,
        ...DEFAULT_HEADERS,
      },
      body: JSON.stringify({userId}),
    });

    const response = await parseActiveUserAddressResponse(result);

    if (!result.ok) {
      return Promise.reject(response);
    }

    return normalizeActiveUserAddress(response);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const ApiUserAddressRepository = {
  setUserAddress,
  viewActiveUserAddressByUserId,
};
